import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchUnshippedOrders, syncOrders } from '../../api/orders';
import { useLatestSyncJob, waitForSyncJob } from '../../api/syncJobs';
import { invalidateCache, useCachedResource } from '../../hooks/useCachedResource';
import { notifyShippingAlertsRefresh } from './shippingAlertsRefresh';
import { saveProcurementDraft } from './procurementDraft';
import {
  Badge,
  EmptyState,
  PageHeader,
  SyncRunBanner,
  StatusBanner,
  PageLoading,
  inputClassName,
  linkClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';
import { OrderLineProducts } from '../../components/ui/OrderLineProducts';
import { ShipByUrgencyAlert } from './ShipByUrgencyAlert';
import { SellerCentralOrderDate } from './orderDateDisplay';
import { getShipByUrgency, getUrgencyRowClassName } from './shipByUrgency';
import { BuyerCancelRequestBanner } from './BuyerCancelRequestBanner';
import { useNow } from './useNow';

const PAGE_SIZE = 25;
/** Soft refresh from CRM DB while the page is open (cron handles Amazon pulls). */
const LIST_POLL_MS = 60_000;
const SYNC_STATUS_POLL_MS = 60_000;

function buildSyncMessage(job, count) {
  const sync = job.result || {};
  const fetched = sync.fetchedFromAmazon ?? 0;
  const synced = sync.synced ?? 0;
  const mode = sync.mode || 'sandbox';
  const parts = [
    `Amazon returned ${fetched} open order(s) (${mode}).`,
    `Saved ${synced} to CRM.`,
    sync.refreshedExisting
      ? `Refreshed ${sync.refreshedExisting} existing unshipped order(s) from Amazon.`
      : null,
    `${count ?? 0} unshipped in database.`,
  ].filter(Boolean);

  if (sync.amazonCounts && Object.keys(sync.amazonCounts).length) {
    const breakdown = Object.entries(sync.amazonCounts)
      .map(([mp, value]) => `${mp}: ${value}`)
      .join(', ');
    parts.push(`By marketplace: ${breakdown}.`);
  }

  if (sync.errors?.length) {
    parts.push(`Errors: ${sync.errors.slice(0, 3).join(' | ')}`);
  }

  return parts.join(' ');
}

function formatShippingAddress(order) {
  const street = [order.shipAddressLine1, order.shipAddressLine2]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ');
  const locality = [order.shipCity, order.shipState].filter(Boolean).join(', ');
  const pin = order.shipPincode?.trim();
  const parts = [street, locality, pin].filter(Boolean);

  return parts.length ? parts.join(' · ') : '—';
}

function hasShippingStreet(order) {
  return Boolean(
    (order.shipAddressLine1 || '').trim() || (order.shipAddressLine2 || '').trim()
  );
}

function formatOrderDate(value) {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function UnshippedOrdersPage({ token, user }) {
  const navigate = useNavigate();
  const now = useNow();
  const [page, setPage] = useState(1);
  const [marketplace, setMarketplace] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [syncing, setSyncing] = useState(false);
  const {
    job: lastSyncJob,
    loading: lastSyncLoading,
    setJob: setLastSyncJob,
    refresh: refreshLastSyncJob,
  } = useLatestSyncJob(token, 'orders_unshipped');

  const cacheKey = useMemo(
    () => `orders-unshipped|${page}|${marketplace || ''}|${search}|${PAGE_SIZE}`,
    [page, marketplace, search]
  );

  const fetcher = useCallback(async () => {
    const params = {
      page: String(page),
      pageSize: String(PAGE_SIZE),
    };
    if (marketplace) params.marketplace = marketplace;
    if (search.trim()) params.search = search.trim();
    return fetchUnshippedOrders(token, params);
  }, [token, page, marketplace, search]);

  const { data, loading, refreshing, reload, error } = useCachedResource(
    token ? cacheKey : null,
    fetcher,
    { staleTime: 45_000 }
  );

  const orders = data?.orders || [];
  const pagination = data?.pagination || {
    page,
    pageSize: PAGE_SIZE,
    total: data?.count ?? 0,
    totalPages: 0,
  };

  useEffect(() => {
    if (syncing) {
      return;
    }
    if (loading && !orders.length) {
      setStatus({ type: 'loading', message: '' });
      return;
    }
    if (error && !orders.length) {
      setStatus({ type: 'error', message: error.message || 'Unable to load unshipped orders.' });
      return;
    }
    if (data && status.type === 'loading' && !status.message) {
      setStatus({ type: 'ready', message: '' });
    }
  }, [loading, error, data, orders.length, status.type, status.message, syncing]);

  const loadOrders = useCallback(
    async ({ silent = false, force = false } = {}) => {
      try {
        await reload({
          force,
          silent: silent || (Boolean(data?.orders?.length) && !force),
        });
      } catch {
        // surfaced via error/status effect
      }
    },
    [reload, data]
  );

  const syncFromAmazon = useCallback(async () => {
    setSyncing(true);
    setStatus({
      type: 'loading',
      message: 'Starting unshipped order sync in the background…',
    });

    try {
      const started = await syncOrders(token, { unshippedOnly: true });
      const jobId = started.jobId || started.job?.id;
      if (!jobId) {
        throw new Error(started.message || 'Unable to start unshipped sync.');
      }

      const job = await waitForSyncJob(token, jobId, {
        onUpdate: () => {
          setStatus((prev) => {
            const message = 'Sync running in the background…';
            if (prev?.type === 'loading' && prev.message === message) {
              return prev;
            }
            return { type: 'loading', message };
          });
        },
      });

      setLastSyncJob(job);

      invalidateCache('orders-unshipped|');
      invalidateCache('orders|');
      notifyShippingAlertsRefresh();

      if (page !== 1) {
        setPage(1);
      } else {
        await loadOrders({ force: true });
      }

      if (job.status === 'failed') {
        setStatus({ type: 'error', message: job.message || job.error || 'Sync failed.' });
        return;
      }

      const hasErrors = Boolean(job.result?.errors?.length);
      setStatus({
        type: hasErrors ? 'error' : 'success',
        message: buildSyncMessage(job, job.result?.synced ?? pagination.total),
      });
    } catch (err) {
      setStatus({ type: 'error', message: err.message });
    } finally {
      setSyncing(false);
    }
  }, [token, page, loadOrders, setLastSyncJob, pagination.total]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch((current) => {
        if (current === searchInput.trim()) {
          return current;
        }
        setPage(1);
        return searchInput.trim();
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [marketplace]);

  // Keep the list fresh from CRM DB; server cron pulls from Amazon every 15 min.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || syncing) {
        return;
      }
      void loadOrders({ silent: true, force: true });
      void refreshLastSyncJob();
    }, LIST_POLL_MS);

    return () => window.clearInterval(timer);
  }, [loadOrders, refreshLastSyncJob, syncing]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      void refreshLastSyncJob();
    }, SYNC_STATUS_POLL_MS);

    return () => window.clearInterval(timer);
  }, [refreshLastSyncJob]);

  const allSelected = useMemo(
    () => orders.length > 0 && selected.size === orders.length,
    [orders.length, selected.size]
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }

    setSelected(new Set(orders.map((order) => order.id)));
  };

  const toggleOne = (orderId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const sendToProcurement = () => {
    const selectedOrders = orders.filter((order) => selected.has(order.id));

    if (!selectedOrders.length) {
      setStatus({ type: 'error', message: 'Select at least one unshipped order.' });
      return;
    }

    const lines = [];
    const ordersWithoutItems = [];

    for (const order of selectedOrders) {
      const items = order.items || [];
      const pendingItems = items.filter((item) => item.procurementStatus === 'pending');
      if (!pendingItems.length) {
        ordersWithoutItems.push(order.amazonOrderId || order.id);
        continue;
      }

      for (const item of pendingItems) {
        if (!item.id) {
          continue;
        }
        lines.push({
          orderId: order.id,
          orderItemId: item.id,
          orderItem: { ...item, order },
          order,
          amazonOrderId: order.amazonOrderId,
          buyerName: order.buyerName,
          quantity: item.quantityOrdered || 1,
          extraQuantity: 0,
          productLink: '',
          supplierNotes: '',
        });
      }
    }

    if (!lines.length) {
      setStatus({
        type: 'error',
        message: ordersWithoutItems.length
          ? `Selected order(s) have no line items yet (${ordersWithoutItems.join(', ')}). Refresh unshipped orders and try again.`
          : 'Selected orders have no line items to send to procurement.',
      });
      return;
    }

    if (ordersWithoutItems.length) {
      setStatus({
        type: 'error',
        message: `Skipped order(s) with no line items: ${ordersWithoutItems.join(', ')}. Refresh those orders before sending.`,
      });
      return;
    }

    saveProcurementDraft(lines);
    navigate('/admin/procurement?from=orders');
  };

  return (
    <section className="grid gap-5">
      <ShipByUrgencyAlert orders={orders} now={now} />

      <PageHeader
        title="Unshipped orders"
        subtitle="Open Amazon orders waiting to ship. Auto-syncs from Amazon every 15 minutes; this list refreshes while the page is open."
        actions={
          <>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={sendToProcurement}
              disabled={!selected.size || syncing}
            >
              Send to procurement ({selected.size})
            </button>
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => loadOrders({ force: true })}
              disabled={loading || refreshing || syncing}
            >
              Refresh
            </button>
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={syncFromAmazon}
              disabled={syncing}
              title="Pull from Amazon now (normally runs automatically every 15 minutes)"
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          </>
        }
      />

      <SyncRunBanner job={lastSyncJob} loading={lastSyncLoading} />

      <StatusBanner status={status} setStatus={setStatus} />

      <div className="grid gap-3 md:grid-cols-3">
        <label className="grid gap-1.5">
          <span className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
            Marketplace
          </span>
          <select
            className={inputClassName}
            value={marketplace}
            onChange={(event) => {
              setMarketplace(event.target.value);
            }}
          >
            <option value="">All</option>
            <option value="IN">India</option>
          </select>
        </label>
        <label className="grid gap-1.5 md:col-span-2">
          <span className="crm-text-muted text-xs font-semibold uppercase tracking-wide">
            Search
          </span>
          <input
            className={inputClassName}
            type="search"
            placeholder="Order ID, buyer, city, product, ASIN, SKU..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
      </div>

      {loading && !orders.length ? (
        <PageLoading label="Loading unshipped orders…" />
      ) : orders.length ? (
        <div className="crm-panel overflow-x-auto rounded-[10px]">
          <table className="w-full min-w-[84rem] border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'rgb(var(--crm-table-head))' }}>
                <th className={`${tableHeadClassName} text-left`}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <th className={`${tableHeadClassName} text-left`}>Order date</th>
                <th className={`${tableHeadClassName} text-left`}>Order</th>
                <th className={`${tableHeadClassName} text-left`}>Product</th>
                <th className={`${tableHeadClassName} text-left`}>Marketplace</th>
                <th className={`${tableHeadClassName} text-left`}>Customer</th>
                <th className={`${tableHeadClassName} text-left`}>Phone</th>
                <th className={`${tableHeadClassName} text-left`}>Shipping address</th>
                <th className={`${tableHeadClassName} text-left`}>Ship by</th>
                <th className={`${tableHeadClassName} text-left`}>Expected delivery</th>
                <th className={`${tableHeadClassName} text-left`}>Total</th>
                <th className={`${tableHeadClassName} text-left`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...orders]
                .sort(
                  (a, b) =>
                    Number(Boolean(b.buyerRequestedCancel)) - Number(Boolean(a.buyerRequestedCancel))
                )
                .map((order) => {
                const urgency = getShipByUrgency(order, now);
                const isUrgent = Boolean(urgency);
                const cancelRequested = Boolean(order.buyerRequestedCancel);

                return (
                <tr
                  key={order.id}
                  className={
                    cancelRequested
                      ? 'bg-[#fff8e7] ring-1 ring-inset ring-amber-200'
                      : getUrgencyRowClassName(urgency)
                  }
                >
                  <td className={tableCellClassName}>
                    <input
                      type="checkbox"
                      checked={selected.has(order.id)}
                      onChange={() => toggleOne(order.id)}
                    />
                  </td>
                  <td className={tableCellClassName}>
                    <SellerCentralOrderDate
                      value={order.orderDate}
                      marketplace={order.marketplace}
                      now={now}
                    />
                  </td>
                  <td className={tableCellClassName}>
                    <div className="grid max-w-md gap-2">
                      <BuyerCancelRequestBanner order={order} />
                      <strong
                        className={`font-semibold ${isUrgent ? 'text-red-400' : 'text-[rgb(var(--crm-text))]'}`}
                      >
                        {order.amazonOrderId}
                      </strong>
                    </div>
                  </td>
                  <td className={tableCellClassName}>
                    <OrderLineProducts order={order} urgent={isUrgent} amazonStyle />
                  </td>
                  <td className={tableCellClassName}>
                    <Badge tone={order.marketplace === 'US' ? 'info' : 'default'}>
                      {order.marketplace}
                    </Badge>
                  </td>
                  <td className={tableCellClassName}>{order.buyerName || '—'}</td>
                  <td className={tableCellClassName}>{order.buyerPhone || '—'}</td>
                  <td className={tableCellClassName}>
                    <div className="grid max-w-xs gap-0.5">
                      <span className="text-sm text-[rgb(var(--crm-text))]">
                        {formatShippingAddress(order)}
                      </span>
                      {!hasShippingStreet(order) ? (
                        <span className="text-[11px] text-amber-700">
                          Street address missing — re-sync to pull full address from Amazon
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className={tableCellClassName}>
                    <span
                      className={[
                        'font-semibold',
                        urgency?.type === 'overdue'
                          ? 'text-red-400'
                          : urgency?.type === 'urgent'
                            ? 'text-red-400'
                            : 'text-[rgb(var(--crm-text))]',
                      ].join(' ')}
                    >
                      {formatOrderDate(order.shipByDate)}
                      {urgency?.type === 'urgent' ? (
                        <span className="mt-0.5 block text-[11px] font-bold uppercase text-red-400">
                          {urgency.hoursLeft}hr left
                        </span>
                      ) : null}
                      {urgency?.type === 'overdue' ? (
                        <span className="mt-0.5 block text-[11px] font-bold uppercase text-red-400">
                          Overdue
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className={tableCellClassName}>
                    <span className="font-medium text-[rgb(var(--crm-text))]">
                      {formatOrderDate(order.deliveryDate)}
                    </span>
                  </td>
                  <td className={tableCellClassName}>
                    {order.currency} {Number(order.totalAmount).toFixed(2)}
                  </td>
                  <td className={tableCellClassName}>
                    <Link to={`/admin/unshipped-orders/${order.id}`} className={linkClassName}>
                      View
                    </Link>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="No unshipped orders"
          description="Amazon returned no Pending, Unshipped, or PartiallyShipped orders in the lookback window. Shipped orders appear on the Orders page — use Sync all orders there."
          action={
            <button
              type="button"
              className={primaryButtonClassName}
              onClick={syncFromAmazon}
              disabled={syncing}
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          }
        />
      )}

      {pagination.totalPages > 1 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className={secondaryButtonClassName}
            disabled={page <= 1 || loading || refreshing || syncing}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span className="crm-text-muted text-sm">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} orders
          </span>
          <button
            type="button"
            className={secondaryButtonClassName}
            disabled={page >= pagination.totalPages || loading || refreshing || syncing}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default UnshippedOrdersPage;
